// AGC: SpraySuitability - Spray decision support with real-time weather data
// AGC: Algorithm checks: wind (3-15 km/h), temp (10-30°C), humidity (50-95%), no rain in 4h
import React, { useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { Wind, Thermometer, Droplets, CloudRain, CheckCircle, AlertTriangle, XCircle, Clock, RefreshCw } from 'lucide-react'
import { TopBar, GlassCard } from '../shared'
import { formatTemperature, formatPercentage, formatWindSpeed } from '@droidfarm/shared'
import { useWeather } from '../../hooks/useWeather'
import { useTranslation } from '../../hooks/useTranslation'
import { useLotes, useFarms } from '../../context'
import type { Screen, SprayConditions, Plot } from '../../types'

interface SpraySuitabilityProps {
  onNavigate: (screen: Screen) => void
  onBack: () => void
  weatherCoordinates?: { lat: number; lon: number }
}

/**
 * AGC: SpraySuitability - Detailed spray conditions analysis
 * AGC: Uses coordinates from the selected farm/lot on WeatherDashboard
 */
export const SpraySuitability: React.FC<SpraySuitabilityProps> = ({ onNavigate, onBack, weatherCoordinates: propCoords }) => {
  const { t } = useTranslation()
  const { currentFarm } = useFarms()
  const { plots } = useLotes()

  // AGC: Read coordinates from React Router state (routed screens use location.state, not navContext)
  const routerLocation = useLocation()
  const routerCoords = (routerLocation.state as { weatherCoordinates?: { lat: number; lon: number } } | null)?.weatherCoordinates

  // AGC: Priority: router state → prop → first lot → farm → undefined (GPS fallback)
  const coordinates = useMemo(() => {
    if (routerCoords) return routerCoords
    if (propCoords) return propCoords
    if (plots && plots.length > 0) {
      for (const lot of plots) {
        if (lot.coordinates && lot.coordinates.length > 0) {
          const lat = lot.coordinates.reduce((s: number, c: [number, number]) => s + c[0], 0) / lot.coordinates.length
          const lon = lot.coordinates.reduce((s: number, c: [number, number]) => s + c[1], 0) / lot.coordinates.length
          if (Math.abs(lat) <= 90 && Math.abs(lon) <= 180) return { lat, lon }
        }
      }
    }
    if (currentFarm?.coordinates && Array.isArray(currentFarm.coordinates)) {
      const [lat, lng] = currentFarm.coordinates
      if (lat && lng && Math.abs(lat) <= 90 && Math.abs(lng) <= 180) return { lat, lon: lng }
    }
    return undefined
  }, [routerCoords, propCoords, currentFarm, plots])

  const { sprayConditions, current, hourly, location, isLoading, error, lastUpdated, refetch } = useWeather({
    coordinates,
  })

  // AGC: Overall status configuration
  const overallConfig = {
    favorable: {
      icon: <CheckCircle className="w-12 h-12 text-green-500" />,
      label: t('weather.conditionsFavorable') || 'Condições Favoráveis',
      bg: 'bg-green-50',
      border: 'border-green-200',
      textColor: 'text-green-700',
    },
    moderate: {
      icon: <AlertTriangle className="w-12 h-12 text-yellow-500" />,
      label: t('weather.conditionsModerate') || 'Condições Moderadas',
      bg: 'bg-yellow-50',
      border: 'border-yellow-200',
      textColor: 'text-yellow-700',
    },
    unfavorable: {
      icon: <XCircle className="w-12 h-12 text-red-500" />,
      label: t('weather.conditionsUnfavorable') || 'Condições Desfavoráveis',
      bg: 'bg-red-50',
      border: 'border-red-200',
      textColor: 'text-red-700',
    },
  }

  // AGC: Loading state
  if (isLoading && !sprayConditions) {
    return (
      <div className="min-h-screen bg-[#F4F5F7]">
        <TopBar 
          title={t('weather.sprayTitle') || 'Pulverização'}
          subtitle={t('weather.spraySubtitle') || 'Análise de condições'}
          onBack={onBack}
        />
        <div className="flex items-center justify-center py-20">
          <RefreshCw className="w-8 h-8 text-[#0B5F4A] animate-spin" />
        </div>
      </div>
    )
  }

  // AGC: Error state
  if (error && !sprayConditions) {
    return (
      <div className="min-h-screen bg-[#F4F5F7]">
        <TopBar 
          title={t('weather.sprayTitle') || 'Pulverização'}
          subtitle={t('weather.error') || 'Erro'}
          onBack={onBack}
        />
        <div className="px-4 py-8">
          <GlassCard className="text-center">
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={() => refetch(true)}
              className="px-4 py-2 bg-[#0B5F4A] text-white rounded-lg"
            >
              {t('weather.retry') || 'Tentar novamente'}
            </button>
          </GlassCard>
        </div>
      </div>
    )
  }

  // AGC: Default spray conditions if not available
  if (!sprayConditions) {
    return (
      <div className="min-h-screen bg-[#F4F5F7]">
        <TopBar 
          title={t('weather.sprayTitle') || 'Pulverização'}
          subtitle={t('weather.noData') || 'Sem dados'}
          onBack={onBack}
        />
        <div className="px-4 py-8">
          <GlassCard className="text-center">
            <p className="text-gray-600">{t('weather.noSprayData') || 'Dados de pulverização não disponíveis'}</p>
          </GlassCard>
        </div>
      </div>
    )
  }

  const status = overallConfig[sprayConditions.overall]

  return (
    <div className="min-h-screen bg-[#F4F5F7]">
      {/* AGC: Top Bar */}
      <TopBar 
        title={t('weather.sprayTitle') || 'Pulverização'}
        subtitle={location || (t('weather.spraySubtitle') || 'Análise de condições')}
        onBack={onBack}
        rightAction={
          <button
            onClick={() => refetch(true)}
            disabled={isLoading}
            className="p-2 rounded-full hover:bg-black/10 transition-colors"
          >
            <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        }
      />

      {/* AGC: Overall Status Card - Compact with time only */}
      <div className="px-4 py-3">
        <GlassCard className={`${status.bg} ${status.border}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
            {status.icon}
              <h2 className={`text-lg font-semibold ${status.textColor}`}>{status.label}</h2>
            </div>
            {sprayConditions.bestWindow && (
              <div className="flex items-center gap-2 bg-white/50 px-3 py-1.5 rounded-lg">
                <Clock className="w-4 h-4 text-gray-600" />
                <span className="font-bold text-gray-800">
                  {sprayConditions.bestWindow.start} - {sprayConditions.bestWindow.end}
                </span>
              </div>
            )}
          </div>
        </GlassCard>
      </div>

      {/* AGC: Delta-T Forecast Chart */}
      <DeltaTForecastChart hourly={hourly || []} t={t} />

      {/* AGC: Delta-T Detailed Section - Right after forecast */}
      <DeltaTDetailedSection 
        sprayConditions={sprayConditions}
        t={t}
      />

      {/* AGC: Condition Analysis */}
      <div className="px-4 space-y-3">
        <h3 className="text-gray-900 font-medium">{t('weather.detailedAnalysis') || 'Análise Detalhada'}</h3>

        {/* AGC: Wind Condition */}
        <ConditionCard
          icon={<Wind className="w-6 h-6 text-blue-500" />}
          title={t('weather.windSpeed') || 'Velocidade do Vento'}
          value={formatWindSpeed(sprayConditions.windSpeed.value)}
          status={sprayConditions.windSpeed.status}
          message={sprayConditions.windSpeed.message}
          idealRange={t('weather.idealWind') || 'Ideal: 3-15 km/h'}
        />

        {/* AGC: Temperature Condition */}
        <ConditionCard
          icon={<Thermometer className="w-6 h-6 text-orange-500" />}
          title={t('weather.temperature') || 'Temperatura'}
          value={formatTemperature(sprayConditions.temperature.value)}
          status={sprayConditions.temperature.status}
          message={sprayConditions.temperature.message}
          idealRange={t('weather.idealTemp') || 'Ideal: 10-30°C'}
        />

        {/* AGC: Humidity Condition */}
        <ConditionCard
          icon={<Droplets className="w-6 h-6 text-cyan-500" />}
          title={t('weather.relativeHumidity') || 'Umidade Relativa'}
          value={formatPercentage(sprayConditions.humidity.value)}
          status={sprayConditions.humidity.status}
          message={sprayConditions.humidity.message}
          idealRange={t('weather.idealHumidity') || 'Ideal: 50-95%'}
        />

        {/* AGC: Rain Condition */}
        <ConditionCard
          icon={<CloudRain className="w-6 h-6 text-gray-500" />}
          title={t('weather.rainForecast') || 'Previsão de Chuva'}
          value={sprayConditions.rain.willRain ? (t('common.yes') || 'Sim') : (t('common.no') || 'Não')}
          status={sprayConditions.rain.status}
          message={sprayConditions.rain.message}
          idealRange={t('weather.idealRain') || 'Ideal: Sem chuva em 4h'}
        />

        {/* AGC: Delta-T Condition */}
        {sprayConditions.deltaT && (
          <ConditionCard
            icon={<span className="text-lg font-bold text-blue-600">ΔT</span>}
            title={t('weather.deltaT') || 'Delta-T'}
            value={`${sprayConditions.deltaT.value.toFixed(1)}°C`}
            status={sprayConditions.deltaT.status}
            message={sprayConditions.deltaT.message}
            idealRange={t('weather.idealDeltaT') || 'Ideal: 2-8°C'}
          />
        )}
      </div>

      {/* AGC: Tips */}
      <div className="px-4 py-6">
        <GlassCard className="bg-blue-50 border-blue-200">
          <h3 className="font-medium text-blue-800 mb-2">💡 {t('weather.sprayTips') || 'Dicas para Pulverização'}</h3>
          <ul className="text-sm text-blue-700 space-y-2">
            <li>• {t('weather.tip1') || 'Evite aplicar com ventos acima de 15 km/h para evitar deriva'}</li>
            <li>• {t('weather.tip2') || 'Temperaturas muito altas causam evaporação do produto'}</li>
            <li>• {t('weather.tip3') || 'Umidade baixa (<50%) reduz a eficácia da aplicação'}</li>
            <li>• {t('weather.tip4') || 'Aguarde pelo menos 4 horas sem chuva após aplicação'}</li>
            <li>• {t('weather.tip5') || 'Prefira aplicar nas primeiras horas da manhã ou final da tarde'}</li>
          </ul>
        </GlassCard>
      </div>

      {/* AGC: Open-Meteo Attribution */}
      <div className="px-4 pb-6 text-center">
        <a 
          href="https://open-meteo.com/" 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
        >
          {t('weather.attribution') || 'Dados meteorológicos por Open-Meteo.com'}
        </a>
      </div>
    </div>
  )
}

/**
 * AGC: Condition Card Component
 */
interface ConditionCardProps {
  icon: React.ReactNode
  title: string
  value: string
  status: 'good' | 'moderate' | 'bad'
  message: string
  idealRange: string
}

const ConditionCard: React.FC<ConditionCardProps> = ({
  icon,
  title,
  value,
  status,
  message,
  idealRange,
}) => {
  const statusColors = {
    good: 'border-l-green-500',
    moderate: 'border-l-yellow-500',
    bad: 'border-l-red-500',
  }

  const statusIcons = {
    good: <CheckCircle className="w-5 h-5 text-green-500" />,
    moderate: <AlertTriangle className="w-5 h-5 text-yellow-500" />,
    bad: <XCircle className="w-5 h-5 text-red-500" />,
  }

  return (
    <GlassCard className={`border-l-4 ${statusColors[status]}`}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0">
          {icon}
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-gray-900">{title}</h3>
            <div className="flex items-center gap-2">
              <span className="font-bold text-gray-900">{value}</span>
              {statusIcons[status]}
            </div>
          </div>
          <p className="text-sm text-gray-600 mt-1">{message}</p>
          <p className="text-xs text-gray-400 mt-1">{idealRange}</p>
        </div>
      </div>
    </GlassCard>
  )
}

/**
 * AGC: Delta-T Interactive Forecast Chart Component
 * AGC: Australian-style psychrometric chart with hour-by-hour slider
 * AGC: X-axis: Dry Temperature (°C), Y-axis: Relative Humidity (%)
 * AGC: Shows current position and allows swiping through forecast hours
 */
interface HourlyData {
  hour: string
  temperature: number
  humidity?: number
  deltaT?: number
  windSpeed?: number
}

interface DeltaTForecastChartProps {
  hourly: HourlyData[]
  t: (key: string) => string
}

const DeltaTForecastChart: React.FC<DeltaTForecastChartProps> = ({ hourly, t }) => {
  const [selectedHourIndex, setSelectedHourIndex] = React.useState(0)
  
  if (!hourly || hourly.length === 0) return null

  // AGC: Get current hour to find starting index
  const currentHour = new Date().getHours()
  const forecastHours = hourly.slice(0, 16)
  
  // AGC: Find current hour index on mount
  React.useEffect(() => {
    const currentIdx = forecastHours.findIndex(h => {
      const hourNum = parseInt(h.hour.split(':')[0])
      return hourNum === currentHour
    })
    if (currentIdx >= 0) setSelectedHourIndex(currentIdx)
  }, [])

  const selectedHour = forecastHours[selectedHourIndex]
  if (!selectedHour) return null

  const temp = selectedHour.temperature
  const humidity = selectedHour.humidity ?? 50
  const deltaT = selectedHour.deltaT ?? 5

  // AGC: Chart dimensions and scales
  const chartWidth = 280
  const chartHeight = 200
  const tempMin = 0
  const tempMax = 50
  const humidityMin = 10
  const humidityMax = 100

  // AGC: Convert temperature to X position
  const tempToX = (t: number) => ((t - tempMin) / (tempMax - tempMin)) * chartWidth
  
  // AGC: Convert humidity to Y position (inverted - 100% at top)
  const humidityToY = (h: number) => chartHeight - ((h - humidityMin) / (humidityMax - humidityMin)) * chartHeight

  // AGC: Calculate Delta-T curves (approximate psychrometric relationship)
  // AGC: Delta-T ≈ f(temperature, humidity) using simplified formula
  const getDeltaTCurvePoints = (targetDeltaT: number) => {
    const points: { x: number; y: number }[] = []
    for (let t = tempMin; t <= tempMax; t += 2) {
      // AGC: Approximate humidity for given temp and Delta-T
      // AGC: Based on psychrometric chart relationships
      const h = Math.max(10, Math.min(100, 100 - (targetDeltaT * 100 / (t * 0.5 + 10))))
      if (h >= humidityMin && h <= humidityMax) {
        points.push({ x: tempToX(t), y: humidityToY(h) })
      }
    }
    return points
  }

  // AGC: Generate SVG path from points
  const pointsToPath = (points: { x: number; y: number }[]) => {
    if (points.length < 2) return ''
    return points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ')
  }

  // AGC: Delta-T curve colors
  const deltaTCurves = [
    { value: 2, color: '#3B82F6', label: '2' },
    { value: 4, color: '#DC2626', label: '4' },
    { value: 6, color: '#F97316', label: '6' },
    { value: 8, color: '#3B82F6', label: '8' },
    { value: 10, color: '#000000', label: '10' },
    { value: 12, color: '#DC2626', label: '12' },
    { value: 14, color: '#F472B6', label: '14' },
  ]

  // AGC: Current position on chart
  const markerX = tempToX(temp)
  const markerY = humidityToY(humidity)

  // AGC: Determine zone color based on Delta-T
  const getZoneInfo = (dt: number) => {
    if (dt >= 2 && dt <= 8) return { color: '#FCD34D', label: t('weather.preferred') || 'Preferido', status: 'good' }
    if (dt < 2) return { color: '#9CA3AF', label: t('weather.tooLow') || 'Muito baixo', status: 'low' }
    if (dt <= 10) return { color: '#9CA3AF', label: t('weather.marginalZone') || 'Marginal', status: 'marginal' }
    return { color: '#FB923C', label: t('weather.unsuitable') || 'Inadequado', status: 'bad' }
  }

  const zoneInfo = getZoneInfo(deltaT)
  const isCurrentHour = selectedHourIndex === forecastHours.findIndex(h => parseInt(h.hour.split(':')[0]) === currentHour)

  return (
    <div className="px-4 mb-4">
      <GlassCard className="overflow-hidden">
        {/* AGC: Chart Header */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className="text-lg font-bold text-blue-600">ΔT</span>
            <h3 className="font-medium text-gray-900">
              {t('weather.deltaTForecast') || 'Previsão Delta-T'}
            </h3>
          </div>
        </div>

        {/* AGC: Selected Hour Display */}
        <div className={`rounded-lg p-3 mb-3 ${
          zoneInfo.status === 'good' ? 'bg-yellow-100' :
          zoneInfo.status === 'bad' ? 'bg-orange-100' : 'bg-gray-100'
        }`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500">
                {isCurrentHour ? (t('weather.now') || 'Agora') : selectedHour.hour}
              </p>
              <p className="text-2xl font-bold text-gray-900">ΔT {deltaT.toFixed(1)}°C</p>
              <p className={`text-sm font-medium ${
                zoneInfo.status === 'good' ? 'text-yellow-700' :
                zoneInfo.status === 'bad' ? 'text-orange-700' : 'text-gray-600'
              }`}>
                {zoneInfo.label}
              </p>
            </div>
            <div className="text-right text-sm text-gray-600">
              <p>{t('weather.temp') || 'Temp'}: <strong>{temp}°C</strong></p>
              <p>{t('weather.humidity') || 'Umidade'}: <strong>{humidity}%</strong></p>
            </div>
          </div>
        </div>

        {/* AGC: Interactive Chart */}
        <div className="relative bg-gradient-to-b from-gray-50 to-white rounded-lg p-2 overflow-hidden">
          <svg 
            viewBox={`-30 -10 ${chartWidth + 50} ${chartHeight + 40}`} 
            className="w-full"
            style={{ height: '200px' }}
          >
            {/* AGC: Zone backgrounds */}
            {/* Preferred zone (2-8) - Yellow */}
            <polygon 
              points={`0,${humidityToY(100)} ${chartWidth},${humidityToY(100)} ${chartWidth},${humidityToY(50)} 0,${humidityToY(70)}`}
              fill="#FEF3C7"
              opacity="0.7"
            />
            {/* Marginal zone - Gray */}
            <polygon 
              points={`0,${humidityToY(70)} ${chartWidth},${humidityToY(50)} ${chartWidth},${humidityToY(30)} 0,${humidityToY(50)}`}
              fill="#E5E7EB"
              opacity="0.5"
            />
            {/* Unsuitable zone - Orange */}
            <polygon 
              points={`0,${humidityToY(50)} ${chartWidth},${humidityToY(30)} ${chartWidth},0 0,0`}
              fill="#FED7AA"
              opacity="0.5"
            />

            {/* AGC: Grid lines */}
            {[10, 20, 30, 40, 50].map(temp => (
              <line 
                key={`temp-${temp}`}
                x1={tempToX(temp)} y1={0} 
                x2={tempToX(temp)} y2={chartHeight}
                stroke="#E5E7EB" strokeWidth="1"
              />
            ))}
            {[20, 40, 60, 80, 100].map(hum => (
              <line 
                key={`hum-${hum}`}
                x1={0} y1={humidityToY(hum)} 
                x2={chartWidth} y2={humidityToY(hum)}
                stroke="#E5E7EB" strokeWidth="1"
              />
            ))}

            {/* AGC: Delta-T curves */}
            {deltaTCurves.map(curve => {
              const points = getDeltaTCurvePoints(curve.value)
              const path = pointsToPath(points)
              return path ? (
                <g key={curve.value}>
                  <path 
                    d={path} 
                    fill="none" 
                    stroke={curve.color} 
                    strokeWidth="1.5"
                    strokeDasharray={curve.value === 2 || curve.value === 8 ? "none" : "4,2"}
                  />
                  {points.length > 0 && (
                    <text 
                      x={points[points.length - 1].x + 3} 
                      y={points[points.length - 1].y} 
                      fill={curve.color}
                      fontSize="10"
                      fontWeight="bold"
                    >
                      {curve.label}
                    </text>
                  )}
                </g>
              ) : null
            })}

            {/* AGC: Current position marker */}
            <circle 
              cx={markerX} 
              cy={markerY} 
              r="8" 
              fill={isCurrentHour ? '#3B82F6' : '#10B981'}
              stroke="white"
              strokeWidth="3"
            />
            <circle 
              cx={markerX} 
              cy={markerY} 
              r="12" 
              fill="none"
              stroke={isCurrentHour ? '#3B82F6' : '#10B981'}
              strokeWidth="2"
              opacity="0.5"
            />

            {/* AGC: X-axis labels (Temperature) */}
            {[0, 10, 20, 30, 40, 50].map(temp => (
              <text 
                key={`label-${temp}`}
                x={tempToX(temp)} 
                y={chartHeight + 15} 
                textAnchor="middle"
                fontSize="10"
                fill="#6B7280"
              >
                {temp}
              </text>
            ))}
            <text 
              x={chartWidth / 2} 
              y={chartHeight + 28} 
              textAnchor="middle"
              fontSize="11"
              fill="#374151"
              fontWeight="500"
            >
              {t('weather.dryTemp') || 'Temperatura (°C)'}
            </text>

            {/* AGC: Y-axis labels (Humidity) */}
            {[20, 40, 60, 80, 100].map(hum => (
              <text 
                key={`hum-label-${hum}`}
                x={-5} 
                y={humidityToY(hum) + 3} 
                textAnchor="end"
                fontSize="10"
                fill="#6B7280"
              >
                {hum}
              </text>
            ))}
            <text 
              x={-25} 
              y={chartHeight / 2} 
              textAnchor="middle"
              fontSize="11"
              fill="#374151"
              fontWeight="500"
              transform={`rotate(-90, -25, ${chartHeight / 2})`}
            >
              {t('weather.relHumidity') || 'Umidade (%)'}
            </text>
          </svg>
        </div>

        {/* AGC: Hour Slider */}
        <div className="mt-3 pt-3 border-t border-gray-100">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs text-gray-500">{t('weather.slideToSee') || 'Deslize para ver previsão'}</span>
          </div>
          
          {/* AGC: Hour Pills */}
          <div className="flex gap-1 overflow-x-auto pb-2 -mx-1 px-1">
            {forecastHours.map((hour, index) => {
              const isSelected = index === selectedHourIndex
              const hourNum = parseInt(hour.hour.split(':')[0])
              const isCurrent = hourNum === currentHour
              const hourDeltaT = hour.deltaT ?? 5
              const hourZone = getZoneInfo(hourDeltaT)

              return (
                <button
                  key={hour.hour}
                  onClick={() => setSelectedHourIndex(index)}
                  className={`flex-shrink-0 px-2 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    isSelected 
                      ? 'bg-blue-500 text-white shadow-md scale-105' 
                      : isCurrent
                        ? 'bg-blue-100 text-blue-700 ring-1 ring-blue-300'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <div className="flex flex-col items-center">
                    <span>{hour.hour.split(':')[0]}h</span>
                    <span className={`text-[10px] ${isSelected ? 'text-white/80' : 'text-gray-400'}`}>
                      ΔT{hourDeltaT.toFixed(0)}
                    </span>
                  </div>
                </button>
              )
            })}
          </div>
        </div>

        {/* AGC: Legend */}
        <div className="flex flex-wrap gap-3 mt-2 text-xs justify-center">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-yellow-200 border border-yellow-400" />
            <span>{t('weather.preferred') || 'Preferido (2-8)'}</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-gray-200 border border-gray-400" />
            <span>{t('weather.marginalZone') || 'Marginal'}</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-orange-200 border border-orange-400" />
            <span>{t('weather.unsuitable') || 'Inadequado'}</span>
          </div>
        </div>
      </GlassCard>
    </div>
  )
}

/**
 * AGC: Delta-T Detailed Section Component
 * AGC: Shows comprehensive Delta-T visualization and explanation
 * AGC: Reference: https://sprayers101.com/delta-t/
 * AGC: Reference: https://www.cropsmart.com.au/blogs/news/using-delta-t-for-assessing-spray-conditions
 */
interface DeltaTDetailedSectionProps {
  sprayConditions: SprayConditions
  t: (key: string) => string
}

const DeltaTDetailedSection: React.FC<DeltaTDetailedSectionProps> = ({ sprayConditions, t }) => {
  const deltaT = sprayConditions.deltaT
  if (!deltaT) return null

  const { value: deltaTValue, wetBulb, dryBulb, status } = deltaT

  // AGC: Generate color for Delta-T zones
  const getZoneColor = (zoneValue: number) => {
    if (zoneValue < 2) return 'bg-blue-400'
    if (zoneValue <= 8) return 'bg-green-400'
    if (zoneValue <= 10) return 'bg-yellow-400'
    return 'bg-red-400'
  }

  // AGC: Calculate indicator position (0-14 scale, mapped to 0-100%)
  const indicatorPosition = Math.min(Math.max((deltaTValue / 14) * 100, 2), 98)

  // AGC: Status colors
  const statusColors = {
    good: 'text-green-600 bg-green-100',
    moderate: 'text-yellow-600 bg-yellow-100',
    bad: 'text-red-600 bg-red-100',
  }

  return (
    <div className="px-4 py-4">
      <GlassCard className="bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-200">
        {/* AGC: Section Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
            <span className="text-xl font-bold text-white">ΔT</span>
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">
              {t('weather.deltaTTitle') || 'Delta-T - Índice de Evaporação'}
            </h3>
            <p className="text-sm text-gray-600">
              {t('weather.deltaTSubtitle') || 'Diferença entre temperatura seca e úmida'}
            </p>
          </div>
        </div>

        {/* AGC: Current Values */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-white/80 rounded-lg p-3 text-center">
            <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-1">
              <Thermometer className="w-4 h-4 text-red-500" />
            </div>
            <p className="text-xs text-gray-500">{t('weather.dryBulb') || 'Bulbo Seco'}</p>
            <p className="text-lg font-bold text-gray-900">{dryBulb}°C</p>
          </div>
          <div className="bg-white/80 rounded-lg p-3 text-center">
            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-1">
              <Droplets className="w-4 h-4 text-blue-500" />
            </div>
            <p className="text-xs text-gray-500">{t('weather.wetBulb') || 'Bulbo Úmido'}</p>
            <p className="text-lg font-bold text-gray-900">{wetBulb.toFixed(1)}°C</p>
          </div>
          <div className={`rounded-lg p-3 text-center ${statusColors[status]}`}>
            <div className="w-8 h-8 rounded-full bg-white/50 flex items-center justify-center mx-auto mb-1">
              <span className="text-sm font-bold">ΔT</span>
            </div>
            <p className="text-xs opacity-80">{t('weather.deltaTValue') || 'Delta-T'}</p>
            <p className="text-lg font-bold">{deltaTValue.toFixed(1)}°C</p>
          </div>
        </div>

        {/* AGC: Visual Scale Chart */}
        <div className="mb-4">
          <p className="text-sm font-medium text-gray-700 mb-2">
            {t('weather.deltaTScale') || 'Escala de Condições'}
          </p>
          
          {/* AGC: Scale bar with zones */}
          <div className="relative h-10 rounded-lg overflow-hidden">
            <div className="absolute inset-0 flex">
              {/* AGC: Zone 0-2: Too Low (Drift Risk) */}
              <div className="w-[14%] bg-blue-400 flex items-center justify-center border-r border-white/50">
                <span className="text-xs text-white font-medium">{'<2'}</span>
              </div>
              {/* AGC: Zone 2-8: Ideal */}
              <div className="w-[43%] bg-green-400 flex items-center justify-center border-r border-white/50">
                <span className="text-xs text-white font-medium">2-8 ✓</span>
              </div>
              {/* AGC: Zone 8-10: Marginal */}
              <div className="w-[14%] bg-yellow-400 flex items-center justify-center border-r border-white/50">
                <span className="text-xs text-white font-medium">8-10</span>
              </div>
              {/* AGC: Zone 10+: Too High (Evaporation) */}
              <div className="w-[29%] bg-red-400 flex items-center justify-center">
                <span className="text-xs text-white font-medium">{'>10'}</span>
              </div>
            </div>
            
            {/* AGC: Current value indicator */}
            <div 
              className="absolute top-0 bottom-0 w-1 bg-gray-800 shadow-xl z-10"
              style={{ left: `${indicatorPosition}%`, transform: 'translateX(-50%)' }}
            >
              <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-4 h-4 bg-gray-800 rounded-full border-2 border-white shadow-lg" />
              <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
                <span className="text-sm font-bold text-gray-800">{deltaTValue.toFixed(1)}°C</span>
              </div>
            </div>
          </div>

          {/* AGC: Zone descriptions */}
          <div className="grid grid-cols-4 gap-1 mt-8 text-xs">
            <div className="text-center">
              <div className="w-3 h-3 rounded-full bg-blue-400 mx-auto mb-1" />
              <p className="text-gray-600">{t('weather.tooLow') || 'Muito baixo'}</p>
              <p className="text-gray-400">{t('weather.driftRisk') || 'Risco de deriva'}</p>
            </div>
            <div className="text-center">
              <div className="w-3 h-3 rounded-full bg-green-400 mx-auto mb-1" />
              <p className="text-gray-600">{t('weather.ideal') || 'Ideal'}</p>
              <p className="text-gray-400">{t('weather.bestConditions') || 'Melhor condição'}</p>
            </div>
            <div className="text-center">
              <div className="w-3 h-3 rounded-full bg-yellow-400 mx-auto mb-1" />
              <p className="text-gray-600">{t('weather.marginal') || 'Marginal'}</p>
              <p className="text-gray-400">{t('weather.useCarefully') || 'Use com cuidado'}</p>
            </div>
            <div className="text-center">
              <div className="w-3 h-3 rounded-full bg-red-400 mx-auto mb-1" />
              <p className="text-gray-600">{t('weather.tooHigh') || 'Muito alto'}</p>
              <p className="text-gray-400">{t('weather.evaporationRisk') || 'Evaporação rápida'}</p>
            </div>
          </div>
        </div>

        {/* AGC: Temperature x Humidity Reference Chart */}
        <div className="mb-4">
          <p className="text-sm font-medium text-gray-700 mb-2">
            {t('weather.deltaTChart') || 'Gráfico de Referência Delta-T'}
          </p>
          <div className="bg-white/80 rounded-lg p-3 overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr>
                  <th className="p-1 text-left text-gray-500">{t('weather.temp') || 'Temp °C'}</th>
                  <th className="p-1 text-center text-gray-500">20%</th>
                  <th className="p-1 text-center text-gray-500">40%</th>
                  <th className="p-1 text-center text-gray-500">60%</th>
                  <th className="p-1 text-center text-gray-500">80%</th>
                </tr>
              </thead>
              <tbody>
                {/* AGC: Simplified Delta-T reference based on Australian chart */}
                <tr>
                  <td className="p-1 font-medium">15°C</td>
                  <td className={`p-1 text-center rounded ${getZoneColor(8)} text-white`}>8</td>
                  <td className={`p-1 text-center rounded ${getZoneColor(5)} text-white`}>5</td>
                  <td className={`p-1 text-center rounded ${getZoneColor(3)} text-white`}>3</td>
                  <td className={`p-1 text-center rounded ${getZoneColor(1)} text-white`}>1</td>
                </tr>
                <tr>
                  <td className="p-1 font-medium">20°C</td>
                  <td className={`p-1 text-center rounded ${getZoneColor(10)} text-white`}>10</td>
                  <td className={`p-1 text-center rounded ${getZoneColor(6)} text-white`}>6</td>
                  <td className={`p-1 text-center rounded ${getZoneColor(4)} text-white`}>4</td>
                  <td className={`p-1 text-center rounded ${getZoneColor(2)} text-white`}>2</td>
                </tr>
                <tr>
                  <td className="p-1 font-medium">25°C</td>
                  <td className={`p-1 text-center rounded ${getZoneColor(12)} text-white`}>12</td>
                  <td className={`p-1 text-center rounded ${getZoneColor(8)} text-white`}>8</td>
                  <td className={`p-1 text-center rounded ${getZoneColor(5)} text-white`}>5</td>
                  <td className={`p-1 text-center rounded ${getZoneColor(2)} text-white`}>2</td>
                </tr>
                <tr>
                  <td className="p-1 font-medium">30°C</td>
                  <td className={`p-1 text-center rounded ${getZoneColor(15)} text-white`}>15</td>
                  <td className={`p-1 text-center rounded ${getZoneColor(10)} text-white`}>10</td>
                  <td className={`p-1 text-center rounded ${getZoneColor(6)} text-white`}>6</td>
                  <td className={`p-1 text-center rounded ${getZoneColor(3)} text-white`}>3</td>
                </tr>
                <tr>
                  <td className="p-1 font-medium">35°C</td>
                  <td className={`p-1 text-center rounded ${getZoneColor(18)} text-white`}>18</td>
                  <td className={`p-1 text-center rounded ${getZoneColor(12)} text-white`}>12</td>
                  <td className={`p-1 text-center rounded ${getZoneColor(7)} text-white`}>7</td>
                  <td className={`p-1 text-center rounded ${getZoneColor(4)} text-white`}>4</td>
                </tr>
              </tbody>
            </table>
            <p className="text-xs text-gray-400 mt-2 text-center">
              {t('weather.humidityAxis') || 'Umidade Relativa (%)'}
            </p>
          </div>
        </div>

        {/* AGC: Delta-T Tips */}
        <div className="bg-white/60 rounded-lg p-3">
          <h4 className="font-medium text-gray-800 mb-2 flex items-center gap-2">
            <span>💡</span>
            {t('weather.deltaTTips') || 'Dicas sobre Delta-T'}
          </h4>
          <ul className="text-sm text-gray-600 space-y-1.5">
            <li className="flex items-start gap-2">
              <span className="w-2 h-2 rounded-full bg-blue-400 mt-1.5 flex-shrink-0" />
              <span>{t('weather.deltaTTip1') || 'Delta-T < 2: Gotas não secam, risco de deriva e lavagem'}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-2 h-2 rounded-full bg-green-400 mt-1.5 flex-shrink-0" />
              <span>{t('weather.deltaTTip2') || 'Delta-T 2-8: Condições ideais para pulverização'}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-2 h-2 rounded-full bg-yellow-400 mt-1.5 flex-shrink-0" />
              <span>{t('weather.deltaTTip3') || 'Delta-T 8-10: Possível com gotas maiores e mais água'}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-2 h-2 rounded-full bg-red-400 mt-1.5 flex-shrink-0" />
              <span>{t('weather.deltaTTip4') || 'Delta-T > 10: Gotas evaporam antes de absorção'}</span>
            </li>
          </ul>
        </div>

        {/* AGC: Source Attribution */}
        <div className="mt-3 text-center">
          <p className="text-xs text-gray-400">
            {t('weather.deltaTSource') || 'Referência:'}{' '}
            <a 
              href="https://sprayers101.com/delta-t/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="underline hover:text-gray-600"
            >
              Sprayers101
            </a>
            {' / '}
            <a 
              href="https://www.cropsmart.com.au/blogs/news/using-delta-t-for-assessing-spray-conditions" 
              target="_blank" 
              rel="noopener noreferrer"
              className="underline hover:text-gray-600"
            >
              CropSmart
            </a>
          </p>
        </div>
      </GlassCard>
    </div>
  )
}

export default SpraySuitability
